import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  BarChart3,
  Building2,
  Calendar,
  DollarSign,
  Download,
  FileText,
  Plus,
  Save,
  Search,
  Tag,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';

interface RegistreClientsPerdusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ServiceKey = 'ti' | 'prog' | 'tel' | 'cyber' | 'bckp';

type ServiceRevenues = Record<ServiceKey, string>;

interface ServoClient {
  id: number;
  name: string;
  alias?: string;
  services: Record<ServiceKey | 'sa' | 'bh', boolean>;
  rates: Record<'ti' | 'prog' | 'tel' | 'cyber' | 'sa' | 'bh', number>;
  primes: { tech: string[]; ventes: string[] };
}

type LostReason =
  | 'Mauvais service'
  | 'Prix'
  | 'Rachat d’entreprise'
  | 'Changement de direction'
  | 'Fin de besoin'
  | 'Compétiteur'
  | 'Autre';

type CompanySize = '0-15 utilisateurs' | '15-50 utilisateurs' | '50 et plus';

interface LostClientEntry {
  id: string;
  clientId?: number | null;
  clientName: string;
  clientAlias?: string;
  serviceRevenues?: ServiceRevenues;
  serviceFlags?: Record<ServiceKey, boolean>;
  lostDate: string;
  reason: LostReason;
  monthlyContract: string;
  sellerPrime: string;
  techPrime: string;
  decisionMaker: string;
  competitorOrBuyer: string;
  companySize: CompanySize;
  notes: string;
  lessons: string;
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'prisme_registre_clients_perdus_v1';

const serviceLabels: Record<ServiceKey, string> = {
  ti: 'TI',
  prog: 'Prog',
  tel: 'TEL',
  cyber: 'Cyber',
  bckp: 'BCKP',
};

const emptyServiceRevenues = (): ServiceRevenues => ({ ti: '', prog: '', tel: '', cyber: '', bckp: '' });

const reasons: LostReason[] = [
  'Mauvais service',
  'Prix',
  'Rachat d’entreprise',
  'Changement de direction',
  'Fin de besoin',
  'Compétiteur',
  'Autre',
];

const reasonColors: Record<LostReason, string> = {
  'Mauvais service': 'bg-red-100 text-red-800 border-red-200',
  Prix: 'bg-amber-100 text-amber-800 border-amber-200',
  'Rachat d’entreprise': 'bg-purple-100 text-purple-800 border-purple-200',
  'Changement de direction': 'bg-blue-100 text-blue-800 border-blue-200',
  'Fin de besoin': 'bg-slate-100 text-slate-800 border-slate-200',
  Compétiteur: 'bg-orange-100 text-orange-800 border-orange-200',
  Autre: 'bg-zinc-100 text-zinc-800 border-zinc-200',
};

const emptyEntry = (): LostClientEntry => {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    clientId: null,
    clientName: '',
    clientAlias: '',
    serviceRevenues: emptyServiceRevenues(),
    serviceFlags: { ti: false, prog: false, tel: false, cyber: false, bckp: false },
    lostDate: new Date().toISOString().slice(0, 10),
    reason: 'Mauvais service',
    monthlyContract: '',
    sellerPrime: '',
    techPrime: '',
    decisionMaker: '',
    competitorOrBuyer: '',
    companySize: '15-50 utilisateurs',
    notes: '',
    lessons: '',
    createdAt: now,
    updatedAt: now,
  };
};

const parseAmount = (value: string) => {
  const amount = Number(value.replace(/[^0-9.,-]/g, '').replace(',', '.'));
  return Number.isNaN(amount) ? 0 : amount;
};

const money = (value: number | string) => {
  const amount = typeof value === 'number' ? value : parseAmount(value);
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(amount);
};

const entryServiceRevenues = (entry: LostClientEntry): ServiceRevenues => ({ ...emptyServiceRevenues(), ...(entry.serviceRevenues || {}) });

const entryMonthlyTotal = (entry: LostClientEntry) => {
  const serviceTotal = Object.values(entryServiceRevenues(entry)).reduce((sum, value) => sum + parseAmount(value), 0);
  return serviceTotal || parseAmount(entry.monthlyContract);
};

const serviceMonthlyTotal = (entries: LostClientEntry[], service: ServiceKey) =>
  entries.reduce((sum, entry) => sum + parseAmount(entryServiceRevenues(entry)[service]), 0);

const csvEscape = (value: string) => `"${value.replaceAll('"', '""')}"`;

const formatDate = (value: string) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('fr-CA', { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(`${value}T12:00:00`));
};

export default function RegistreClientsPerdusModal({ isOpen, onClose }: RegistreClientsPerdusModalProps) {
  const [entries, setEntries] = useState<LostClientEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState<'Toutes' | LostReason>('Toutes');
  const [companySizeFilter, setCompanySizeFilter] = useState<'Toutes' | CompanySize>('Toutes');
  const [servoClients, setServoClients] = useState<ServoClient[]>([]);
  const [servoSearch, setServoSearch] = useState('');
  const [servoError, setServoError] = useState<string | null>(null);
  const [isLoadingServoClients, setIsLoadingServoClients] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = (JSON.parse(raw) as LostClientEntry[]).map(entry => ({
          ...entry,
          serviceRevenues: { ...emptyServiceRevenues(), ...(entry.serviceRevenues || {}) },
          serviceFlags: { ti: false, prog: false, tel: false, cyber: false, bckp: false, ...(entry.serviceFlags || {}) },
          companySize: (entry as LostClientEntry).companySize || '15-50 utilisateurs',
        }));
        setEntries(parsed);
        setSelectedId(parsed[0]?.id ?? null);
        return;
      } catch (error) {
        console.warn('Impossible de charger le registre des clients perdus', error);
      }
    }
    const firstEntry = emptyEntry();
    setEntries([firstEntry]);
    setSelectedId(firstEntry.id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }, [entries, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    setIsLoadingServoClients(true);
    setServoError(null);

    fetch('/api/prisme/servo2/clients?limit=200', { credentials: 'include', signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('Impossible de charger les clients Servo2');
        return response.json();
      })
      .then(data => setServoClients(Array.isArray(data.clients) ? data.clients : []))
      .catch(error => {
        if (error.name !== 'AbortError') setServoError(error.message || 'Erreur Servo2');
      })
      .finally(() => setIsLoadingServoClients(false));

    return () => controller.abort();
  }, [isOpen]);


  const selectedEntry = entries.find(entry => entry.id === selectedId) ?? null;

  const filteredEntries = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return entries.filter(entry => {
      const matchesReason = reasonFilter === 'Toutes' || entry.reason === reasonFilter;
      const matchesCompanySize = companySizeFilter === 'Toutes' || entry.companySize === companySizeFilter;
      const haystack = [
        entry.clientName,
        entry.reason,
        entry.sellerPrime,
        entry.techPrime,
        entry.decisionMaker,
        entry.competitorOrBuyer,
        entry.notes,
        entry.lessons,
      ]
        .join(' ')
        .toLowerCase();
      return matchesReason && matchesCompanySize && (!normalizedSearch || haystack.includes(normalizedSearch));
    });
  }, [companySizeFilter, entries, reasonFilter, search]);

  const stats = useMemo(() => {
    const totalMonthly = entries.reduce((sum, entry) => sum + entryMonthlyTotal(entry), 0);
    const tiRevenue = serviceMonthlyTotal(entries, 'ti');
    const serviceLosses = entries.filter(entry => entry.reason === 'Mauvais service').length;
    const byReason = reasons.map(reason => ({
      reason,
      count: entries.filter(entry => entry.reason === reason).length,
      amount: entries
        .filter(entry => entry.reason === reason)
        .reduce((sum, entry) => sum + entryMonthlyTotal(entry), 0),
    }));
    return {
      totalClients: entries.length,
      totalMonthly,
      tiRevenue,
      annualizedLoss: totalMonthly * 12,
      serviceLosses,
      byReason,
    };
  }, [entries]);

  const maxReasonCount = Math.max(1, ...stats.byReason.map(item => item.count));

  const updateEntry = (id: string, patch: Partial<LostClientEntry>) => {
    setEntries(current =>
      current.map(entry =>
        entry.id === id ? { ...entry, ...patch, updatedAt: new Date().toISOString() } : entry
      )
    );
  };

  const linkServoClient = (client: ServoClient) => {
    if (!selectedEntry) return;
    updateEntry(selectedEntry.id, {
      clientId: client.id,
      clientName: client.name,
      clientAlias: client.alias || '',
      sellerPrime: client.primes.ventes[0] || selectedEntry.sellerPrime,
      techPrime: client.primes.tech[0] || selectedEntry.techPrime,
      serviceFlags: {
        ti: client.services.ti,
        prog: client.services.prog,
        tel: client.services.tel,
        cyber: client.services.cyber,
        bckp: client.services.bckp,
      },
    });
    setServoSearch(client.name);
  };

  const visibleServoClients = useMemo(() => {
    const query = servoSearch.trim().toLowerCase();
    return servoClients
      .filter(client => !query || `${client.name} ${client.alias || ''} ${client.id}`.toLowerCase().includes(query))
      .slice(0, 12);
  }, [servoClients, servoSearch]);

  const updateServiceRevenue = (service: ServiceKey, value: string) => {
    if (!selectedEntry) return;
    updateEntry(selectedEntry.id, {
      serviceRevenues: {
        ...entryServiceRevenues(selectedEntry),
        [service]: value,
      },
      monthlyContract: String(
        Object.entries({ ...entryServiceRevenues(selectedEntry), [service]: value }).reduce(
          (sum, [, amount]) => sum + parseAmount(amount),
          0
        )
      ),
    });
  };

  const addEntry = () => {
    const entry = emptyEntry();
    setEntries(current => [entry, ...current]);
    setSelectedId(entry.id);
  };

  const deleteEntry = (id: string) => {
    setEntries(current => {
      const next = current.filter(entry => entry.id !== id);
      if (selectedId === id) setSelectedId(next[0]?.id ?? null);
      return next;
    });
  };

  const exportCsv = () => {
    const headers = [
      'ID client Servo2',
      'Client',
      'Revenu TI',
      'Revenu Prog',
      'Revenu TEL',
      'Revenu Cyber',
      'Revenu BCKP',
      'Revenu global mensuel',
      'Perte annualisée',
      'Date perte',
      'Raison',
      'Contrat mensuel',
      'Vendeur prime',
      'Tech prime',
      'Décideur',
      'Compétiteur / acheteur',
      'Grosseur entreprise',
      'Notes',
      'Leçons apprises',
    ];
    const rows = entries.map(entry => [
      String(entry.clientId || ''),
      entry.clientName,
      entryServiceRevenues(entry).ti,
      entryServiceRevenues(entry).prog,
      entryServiceRevenues(entry).tel,
      entryServiceRevenues(entry).cyber,
      entryServiceRevenues(entry).bckp,
      String(entryMonthlyTotal(entry)),
      String(entryMonthlyTotal(entry) * 12),
      entry.lostDate,
      entry.reason,
      entry.monthlyContract,
      entry.sellerPrime,
      entry.techPrime,
      entry.decisionMaker,
      entry.competitorOrBuyer,
      entry.companySize,
      entry.notes,
      entry.lessons,
    ]);
    const csv = [headers, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `registre-clients-perdus-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            className="relative w-full max-w-[1500px] max-h-[94vh] overflow-hidden rounded-3xl bg-white shadow-2xl"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 z-20 w-10 h-10 rounded-full bg-white/90 text-slate-700 hover:bg-white shadow flex items-center justify-center"
              aria-label="Fermer le registre"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative overflow-hidden bg-linear-to-br from-slate-950 via-blue-950 to-orange-700 p-7 text-white">
              <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_white,_transparent_35%)]" />
              <div className="relative flex flex-wrap items-end justify-between gap-4 pr-12">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-orange-200/40 bg-orange-400/20 px-4 py-2 text-sm font-semibold text-orange-50 mb-4">
                    <BarChart3 className="w-4 h-4" />
                    Tableau de bord — Opérations / Service & Direction TI
                  </div>
                  <h2 className="text-4xl font-black mb-2">Registre des clients perdus</h2>
                  <p className="max-w-3xl text-sm leading-7 text-white/85">
                    Suivi centralisé des pertes clients : causes, revenus mensuels perdus par service, responsables primes,
                    grosseur de l’entreprise, notes terrain et apprentissages pour améliorer la rétention.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20">
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                  <button onClick={addEntry} className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold text-blue-950 hover:bg-blue-50">
                    <Plus className="w-4 h-4" /> Ajouter un client
                  </button>
                </div>
              </div>
            </div>

            <div className="max-h-[calc(94vh-178px)] overflow-y-auto bg-slate-50 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
                {[
                  { label: 'Clients perdus', value: stats.totalClients, icon: Building2, accent: 'border-blue-500' },
                  { label: 'Revenu global mensuel', value: money(stats.totalMonthly), icon: DollarSign, accent: 'border-red-500' },
                  { label: 'Perte annualisée', value: money(stats.annualizedLoss), icon: Calendar, accent: 'border-orange-500' },
                  { label: 'Revenu service TI', value: money(stats.tiRevenue), icon: Users, accent: 'border-emerald-500' },
                  { label: 'Perte liée au service', value: stats.serviceLosses, icon: AlertTriangle, accent: 'border-purple-500' },
                ].map(card => {
                  const Icon = card.icon;
                  return (
                    <div key={card.label} className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm border-l-4 ${card.accent}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{card.label}</p>
                          <p className="mt-1 text-2xl font-black text-slate-900">{card.value}</p>
                        </div>
                        <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                          <Icon className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[1fr_430px] gap-6">
                <section className="space-y-5">
                  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-xl font-black text-slate-900">Répartition des raisons</h3>
                        <p className="text-sm text-slate-500">Vue rapide des patterns : service, prix, rachat, compétition, etc.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {stats.byReason.map(item => (
                        <button
                          key={item.reason}
                          type="button"
                          onClick={() => setReasonFilter(reasonFilter === item.reason ? 'Toutes' : item.reason)}
                          className={`rounded-2xl border p-4 text-left transition ${reasonFilter === item.reason ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
                        >
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <span className={`rounded-full border px-3 py-1 text-xs font-bold ${reasonColors[item.reason]}`}>{item.reason}</span>
                            <span className="text-sm font-black text-slate-900">{item.count}</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                            <div className="h-full rounded-full bg-blue-600" style={{ width: `${(item.count / maxReasonCount) * 100}%` }} />
                          </div>
                          <div className="mt-2 text-xs font-semibold text-slate-500">Revenu mensuel : {money(item.amount)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="border-b border-slate-200 p-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[240px]">
                          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                          <input
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Chercher client, prime, compétiteur, note..."
                          />
                        </div>
                        <select
                          value={reasonFilter}
                          onChange={event => setReasonFilter(event.target.value as 'Toutes' | LostReason)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Toutes">Toutes les raisons</option>
                          {reasons.map(reason => <option key={reason} value={reason}>{reason}</option>)}
                        </select>
                        <select
                          value={companySizeFilter}
                          onChange={event => setCompanySizeFilter(event.target.value as 'Toutes' | CompanySize)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Toutes">Toutes les grosseurs</option>
                          <option value="0-15 utilisateurs">0-15 utilisateurs</option>
                          <option value="15-50 utilisateurs">15-50 utilisateurs</option>
                          <option value="50 et plus">50 et plus</option>
                        </select>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[980px] text-sm">
                        <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-4 py-3 text-left">Client</th>
                            <th className="px-4 py-3 text-left">Date</th>
                            <th className="px-4 py-3 text-left">Raison</th>
                            <th className="px-4 py-3 text-right">Mensuel</th>
                            <th className="px-4 py-3 text-left">Vendeur prime</th>
                            <th className="px-4 py-3 text-left">Tech prime</th>
                            <th className="px-4 py-3 text-left">Grosseur</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {filteredEntries.map(entry => (
                            <tr
                              key={entry.id}
                              onClick={() => setSelectedId(entry.id)}
                              className={`cursor-pointer hover:bg-blue-50/70 ${selectedId === entry.id ? 'bg-blue-50' : ''}`}
                            >
                              <td className="px-4 py-3 font-bold text-slate-900">{entry.clientName || 'Nouveau client perdu'}</td>
                              <td className="px-4 py-3 text-slate-600">{formatDate(entry.lostDate)}</td>
                              <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${reasonColors[entry.reason]}`}>{entry.reason}</span></td>
                              <td className="px-4 py-3 text-right font-black text-slate-900">{money(entryMonthlyTotal(entry))}</td>
                              <td className="px-4 py-3 text-slate-700">{entry.sellerPrime || '—'}</td>
                              <td className="px-4 py-3 text-slate-700">{entry.techPrime || '—'}</td>
                              <td className="px-4 py-3"><span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800">{entry.companySize}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {filteredEntries.length === 0 && (
                      <div className="p-10 text-center text-slate-500">
                        <FileText className="w-10 h-10 mx-auto mb-3" />
                        Aucun client ne correspond aux filtres.
                      </div>
                    )}
                  </div>
                </section>

                <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm h-fit sticky top-4">
                  {selectedEntry ? (
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-xl font-black text-slate-900">Fiche de suivi</h3>
                          <p className="text-xs text-slate-500">Clique une ligne du registre pour l’éditer.</p>
                        </div>
                        <button onClick={() => deleteEntry(selectedEntry.id)} className="rounded-xl border border-red-200 p-2 text-red-700 hover:bg-red-50" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="space-y-2">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Client Servo2 lié</span>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                          <input
                            value={servoSearch || selectedEntry.clientName}
                            onChange={event => setServoSearch(event.target.value)}
                            className="w-full rounded-2xl border border-slate-200 pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Chercher un client Servo2"
                          />
                        </div>
                        {servoError && <p className="text-xs font-semibold text-red-600">{servoError}</p>}
                        {isLoadingServoClients && <p className="text-xs text-slate-500">Chargement des clients Servo2...</p>}
                        <div className="max-h-36 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50">
                          {visibleServoClients.map(client => (
                            <button
                              key={client.id}
                              type="button"
                              onClick={() => linkServoClient(client)}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${selectedEntry.clientId === client.id ? 'bg-blue-50 font-bold text-blue-900' : 'text-slate-700'}`}
                            >
                              <span>{client.name}</span>
                              {client.alias && <span className="ml-2 text-xs text-slate-500">{client.alias}</span>}
                            </button>
                          ))}
                        </div>
                        {selectedEntry.clientId && <p className="text-xs font-semibold text-emerald-700">Lié au client Servo2 #{selectedEntry.clientId}</p>}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <label className="space-y-1 block">
                          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Date</span>
                          <input type="date" value={selectedEntry.lostDate} onChange={event => updateEntry(selectedEntry.id, { lostDate: event.target.value })} className="w-full rounded-2xl border border-slate-200 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </label>
                        <label className="space-y-1 block">
                          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Total mensuel</span>
                          <input value={money(entryMonthlyTotal(selectedEntry))} readOnly className="w-full rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3 font-bold text-slate-700" />
                        </label>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Revenus mensuels par pastille</span>
                          <span className="text-xs font-black text-slate-900">Total {money(entryMonthlyTotal(selectedEntry))}</span>
                        </div>

                        {(() => {
                          const revenues = entryServiceRevenues(selectedEntry);
                          const visibleServices = (Object.keys(serviceLabels) as ServiceKey[]).filter(service => parseAmount(revenues[service]) > 0);

                          return (
                            <>
                              {visibleServices.length > 0 && (
                                <div className="grid grid-cols-2 gap-2 mb-3">
                                  {visibleServices.map(service => (
                                    <div key={service} className="rounded-xl border border-blue-100 bg-white px-3 py-2">
                                      <div className="text-[11px] font-black uppercase tracking-wide text-blue-700">{serviceLabels[service]}</div>
                                      <div className="text-sm font-black text-slate-900">{money(revenues[service])}</div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <details className="rounded-xl border border-slate-200 bg-white p-3">
                                <summary className="cursor-pointer text-xs font-bold text-slate-600">Ajouter / modifier les revenus</summary>
                                <div className="grid grid-cols-2 gap-2 mt-3">
                                  {(Object.keys(serviceLabels) as ServiceKey[]).map(service => (
                                    <label key={service} className="space-y-1">
                                      <span className={`text-[11px] font-bold ${selectedEntry.serviceFlags?.[service] ? 'text-blue-700' : 'text-slate-500'}`}>
                                        {serviceLabels[service]} {selectedEntry.serviceFlags?.[service] ? '• actif Servo2' : ''}
                                      </span>
                                      <input
                                        value={revenues[service]}
                                        onChange={event => updateServiceRevenue(service, event.target.value)}
                                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                      />
                                    </label>
                                  ))}
                                </div>
                              </details>
                            </>
                          );
                        })()}
                      </div>

                      <label className="space-y-1 block">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Raison principale</span>
                        <select value={selectedEntry.reason} onChange={event => updateEntry(selectedEntry.id, { reason: event.target.value as LostReason })} className="w-full rounded-2xl border border-slate-200 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {reasons.map(reason => <option key={reason} value={reason}>{reason}</option>)}
                        </select>
                      </label>

                      <div className="grid grid-cols-2 gap-3">
                        <label className="space-y-1 block">
                          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Vendeur prime</span>
                          <div className="relative">
                            <UserRound className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                            <input value={selectedEntry.sellerPrime} onChange={event => updateEntry(selectedEntry.id, { sellerPrime: event.target.value })} className="w-full rounded-2xl border border-slate-200 pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Nom" />
                          </div>
                        </label>
                        <label className="space-y-1 block">
                          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Tech prime</span>
                          <div className="relative">
                            <Users className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                            <input value={selectedEntry.techPrime} onChange={event => updateEntry(selectedEntry.id, { techPrime: event.target.value })} className="w-full rounded-2xl border border-slate-200 pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Nom" />
                          </div>
                        </label>
                      </div>

                      <label className="space-y-1 block">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Décideur / contact clé</span>
                        <input value={selectedEntry.decisionMaker} onChange={event => updateEntry(selectedEntry.id, { decisionMaker: event.target.value })} className="w-full rounded-2xl border border-slate-200 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Nom, rôle, contexte" />
                      </label>

                      <label className="space-y-1 block">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Compétiteur / acheteur</span>
                        <input value={selectedEntry.competitorOrBuyer} onChange={event => updateEntry(selectedEntry.id, { competitorOrBuyer: event.target.value })} className="w-full rounded-2xl border border-slate-200 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Nouveau fournisseur, acquéreur..." />
                      </label>

                      <label className="space-y-1 block">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Grosseur de l’entreprise</span>
                        <div className="grid grid-cols-3 gap-2">
                          {(['0-15 utilisateurs', '15-50 utilisateurs', '50 et plus'] as const).map(size => (
                            <button key={size} type="button" onClick={() => updateEntry(selectedEntry.id, { companySize: size })} className={`rounded-2xl border px-3 py-2 text-xs font-bold ${selectedEntry.companySize === size ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                              {size}
                            </button>
                          ))}
                        </div>
                      </label>

                      <label className="space-y-1 block">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Notes</span>
                        <textarea value={selectedEntry.notes} onChange={event => updateEntry(selectedEntry.id, { notes: event.target.value })} className="min-h-28 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Historique, perception, signaux faibles..." />
                      </label>

                      <label className="space-y-1 block">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Leçons apprises</span>
                        <textarea value={selectedEntry.lessons} onChange={event => updateEntry(selectedEntry.id, { lessons: event.target.value })} className="min-h-24 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ce qu’on doit changer ou surveiller..." />
                      </label>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2 text-emerald-900">
                        <Save className="w-4 h-4 mt-0.5" />
                        <p className="text-xs leading-5"><strong>Sauvegarde automatique.</strong> Pour un registre partagé multi-utilisateurs, prochaine étape : backend/table serveur.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-slate-500">
                      <Tag className="w-10 h-10 mx-auto mb-3" />
                      <p>Aucune fiche sélectionnée.</p>
                      <button onClick={addEntry} className="mt-4 rounded-xl bg-blue-600 px-4 py-2 text-white font-semibold hover:bg-blue-700">Créer une fiche</button>
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
